import React from 'react';
import {
    Avatar,
    Box,
    FlexBox,
    ModalDialog,
    ModalDialogBody,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    Spinner,
    Typography,
} from '@vp/swan';
import { __ } from '@99designs/i18n';
import {
    PreferredDesignersListQuery,
    usePreferredDesignersListQuery,
} from './PreferredDesignersList.generated';
import { PREFERRED_DESIGNERS_PAGE_SIZE } from './preferredDesigners';

type Designer = PreferredDesignersListQuery['myDesigners']['designers'][number];

export interface PreferredDesignersModalProps {
    isOpen: boolean;
    onRequestDismiss: () => void;
}

export function PreferredDesignersModal({
    isOpen,
    onRequestDismiss,
}: PreferredDesignersModalProps) {
    const { data, loading, error } = usePreferredDesignersListQuery({
        variables: { page: 1, pageSize: PREFERRED_DESIGNERS_PAGE_SIZE },
        skip: !isOpen,
        errorPolicy: 'all',
    });

    const designers = data?.myDesigners.designers ?? [];

    return (
        <ModalDialog
            variant="standard"
            isOpen={isOpen}
            onRequestDismiss={onRequestDismiss}
            onlyRenderWhenOpen
        >
            <ModalDialogContent aria-labelledby="modal dialog panel" style={{ width: '480px' }}>
                <ModalDialogNav>
                    <ModalDialogCloseButton accessibleText={__('Close')} />
                </ModalDialogNav>
                <ModalDialogHeader>
                    <ModalDialogTitle>{__('My preferred designers')}</ModalDialogTitle>
                </ModalDialogHeader>
                <ModalDialogBody>
                    <ModalBodyContent
                        loading={loading}
                        error={Boolean(error)}
                        designers={designers}
                    />
                </ModalDialogBody>
            </ModalDialogContent>
        </ModalDialog>
    );
}

interface ModalBodyContentProps {
    loading: boolean;
    error: boolean;
    designers: ReadonlyArray<Designer>;
}

function ModalBodyContent({ loading, error, designers }: ModalBodyContentProps) {
    if (loading) {
        return (
            <FlexBox justifyContent="center" mt={5}>
                <Spinner accessibleText={__('Loading')} size="super" />
            </FlexBox>
        );
    }

    if (designers.length === 0 && error) {
        return (
            <Typography fontSkin="body-standard" textColor="error">
                {__("We couldn't load your preferred designers. Please try again later.")}
            </Typography>
        );
    }

    if (designers.length === 0) {
        return (
            <Typography fontSkin="body-standard">
                {__("You haven't worked with any designers yet.")}
            </Typography>
        );
    }

    return (
        <FlexBox flexDirection="column" gap="between-actions">
            {designers.map((designer) => {
                const displayName = designer.user?.displayName ?? __('Designer');
                const avatarUrl = designer.user?.profile.avatarUrl;
                return (
                    <FlexBox
                        key={designer.userCanonicalId}
                        alignItems="center"
                        gap="between-actions"
                    >
                        {avatarUrl && (
                            <Box>
                                <Avatar src={avatarUrl} alt={displayName} />
                            </Box>
                        )}
                        <Typography fontSkin="body-standard-bold">{displayName}</Typography>
                    </FlexBox>
                );
            })}
        </FlexBox>
    );
}
